// §4.4's FAILURE-ACTION SHEET (07-B-3) — what happens to the material, decided
// where the failure was recorded. Rendered by the ONE execution screen in both
// shells for an inspection that is `failed` with `resolution_status='pending'`.
//
// FOUR BUTTONS, and the count is a decision, not a draft:
//
//   Rework            POST /inspections/{id}/resolve kind=rework   (§3.5a)
//   Scrap             POST /inspections/{id}/resolve kind=scrap    (§3.5b)
//   Quarantine        POST /holds — §2.7's manual door, the inspection's own
//                     subject; the server ATTACHES to an existing active
//                     quarantine rather than minting a second (§2.7)
//   Accept deviation  POST /inspections/{id}/resolve kind=accept_deviation
//                     (§3.5e — mandatory note, contract-enforced)
//
// THE FIFTH IS GUIDED CUT, AND IT LANDED WITH 07-C (D-158/D-167/D-181/D-182).
// The paragraph that stood here recorded its ABSENCE as a decision — "an
// unbuilt slice is not a control" — and that absence is now spent: the cut
// tables, the five routes and §4.5's screen are live. It renders from the
// inspection read's `guided_cut` block and from nothing else (see
// guided-cut-action.tsx for why a client re-derivation is the one thing this
// control may not do), in four classes: absent for no grant and for a check
// that offers no cut, dead-with-its-reason for five, dead-with-its-reason-and-
// the-re-measure for two, live for `eligible: true`.
//
// AUTHORITY DECIDES EXISTENCE (D-118): scrap and accept-deviation need
// `quality.resolution.dispose` (qc only), rework `quality.resolution.rework`
// (fa/qc/planner), quarantine `quality.hold.place` — a button whose code this
// principal lacks is ABSENT. Connectivity decides LIVENESS: the sheet is
// ONLINE-ONLY on both shells (§4.4's dated sentence — a disposition consumes a
// real subject and drives modules 04/05/06 in one transaction, so it never
// queues), and offline every offered control stays and carries its reason
// INSIDE the slab, the FloorButton pattern.
//
// REWORK IS JUDGED ON THE CHOSEN TARGET'S ORDER (D-299 ruling G5, narrowed by
// owner ruling R8 on 2026-09-25; spec 07 §4.4). §3.5a refuses a rework whose
// target's work order is not `released`/`in_progress`
// (`QUALITY_REWORK_TARGET_NOT_REOPENABLE`, `blocked_by: work_order_not_live`),
// and the server decides on the TARGET it is sent: the explicit
// `work_order_operation_id` when the form names one, the inspection's own
// operation otherwise (holds.service.ts `executeReworkInTxn`). The inspection
// read's `rework_target_work_order_status` is the second of those only — the
// order of the step a rework with NO explicit ref would re-open. The first cut
// of G5 read it as the verdict on Rework itself and disabled the button, which
// also made the explicit-target field unreachable: a qc sending a final-stage
// failure back to a step on a LIVE order (§3.5a's own case) was blocked on the
// client for a rework the server would accept (round-2 refutation). So:
//
//  * the Rework BUTTON is never dead for the order's reason. On a finished own
//    order it stays clickable and carries that fact inside the slab
//    (`data-reason="order_not_live"`), so the reader knows before opening the
//    form that the default target is gone;
//  * the TARGET FIELD is always live inside the open form;
//  * CONFIRM is what the order blocks, judged on the CHOSEN target. When the
//    inspection read already says the own step's order is finished, that
//    read answers and nothing is asked. Otherwise the sheet asks the door
//    itself — `arm.reworkTarget`, a read of `GET …/rework-target` that runs
//    module 04's own verdict on exactly the step the door would pick (a step
//    named in the field by its id; the own step with no ref, round 4) — and
//    Confirm waits for the answer, then carries the door's reason (order not
//    live, step not completed, no such step, or a HELD step this caller may
//    not rework, with D-133's who-sentence) or goes live. A preview that
//    fails for any other reason is not a verdict: Confirm goes live and the
//    door's refusal, if any, is relayed. The verdict and its sentence come
//    from `rework-verdict.ts`, which the §4.6 dialog reads too (owner ruling
//    Q4), so the two surfaces cannot word one door two ways.
//
// Offline is now the reason the button is dead, so it is the reason the slab
// shows: reconnecting DOES open a path (a named step on a live order), which
// was not true when the order's reason killed the whole control. `null` (no
// known target) and an ABSENT key (a cached floor read from before the field)
// judge nothing, as before.
//
// REFUSALS ARE RELAYED, NEVER GENERICIZED. The 409/422 bodies here are
// decisions with continuations in them — QUALITY_SCRAP_ALREADY_ATTRIBUTED
// names the hand-off and offers accept_deviation/rework/downgrade,
// QUALITY_REWORK_REQUIRES_HOLD_RELEASE names the role set that may clear the
// hold, QUALITY_SCRAP_CAUSE_UNMAPPED names the failure cause — and the sheet
// renders the server's own sentences with the request id.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { formatMinor } from "../../production/lib/format";
import {
  reworkTargetOrderNotLive,
  type QualityInspection,
  type QualityInspectionGuidedCut,
  type QualityReworkTargetWorkOrderStatus,
} from "../hooks/quality-queries";
import { GuidedCutAction, guidedCutRender } from "./guided-cut-action";
import type { FailureActionArm } from "./inspection-run-port";
import {
  reworkBlockedSentence,
  reworkPreviewOfOutcome,
  reworkVerdictOf,
  type ReworkPreview,
  type ReworkVerdict,
} from "./rework-verdict";

/** The §7 grants the sheet renders from — derived by BOTH hosts from the one
 * mirror in hooks/permissions.ts (fail-open on a FAILED feed, like
 * `canRecord`: the server is the boundary and renders its own refusal). */
export interface FailureActionGrants {
  /** `quality.resolution.rework` — fa / qc / planner (+ga). */
  readonly canRework: boolean;
  /** `quality.resolution.dispose` — qc (+ga): scrap AND accept-deviation. */
  readonly canDispose: boolean;
  /** `quality.hold.place` — fa / qc / storekeeper (+ga). */
  readonly canQuarantine: boolean;
  /** `quality.cut.execute` — ga / fa / qc / floor_worker (§7's "Guided cut"
   * row; cancel is narrowed SERVICE-side, D-163, not by a second code). Absent
   * ⇒ the fifth control is ABSENT, exactly as the other four behave. */
  readonly canCut: boolean;
  /** `inventory.scan.locate` — MODULE 06's code, because the in-place fix for
   * two of the nine ineligibility reasons is module 06's own re-measure route.
   * A grant from another module's mirror, carried here rather than re-derived,
   * so the sheet asks the same question the server will. */
  readonly canRemeasure: boolean;
}

export type SheetAction = "rework" | "scrap" | "quarantine" | "accept_deviation";

/** What the last taken action came to — held by the SCREEN (not the sheet) so
 * the refs survive the refetch that replaces the pending sheet with the
 * resolved line. */
export type SheetOutcome =
  | {
      readonly kind: "resolved";
      readonly action: "rework" | "scrap" | "accept_deviation";
      readonly reworkOperationId: string | null;
      readonly wasteEventId: string | null;
      readonly holdId: string | null;
      /** Present only inside module 05's own visibility set (§7's second
       * bullet — the field travels with its owner's rule); the note LABELS
       * the omission client-side, per §7. */
      readonly scrapValuation:
        { readonly costMinor: number; readonly currency: string } | undefined;
    }
  | { readonly kind: "quarantined"; readonly holdNo: string }
  | {
      readonly kind: "refused";
      readonly action: SheetAction;
      readonly message: string;
      readonly detail: string | undefined;
      readonly requestId: string | undefined;
    };

/** Loose uuid shape for the rework target field — a half-typed id must not be
 * sent as a ref that 422s (the waste area's UUID_SHAPE rule). Exported for the
 * §4.6 dialog's rework-target field (fix round A1), which validates the same
 * ref for the same route family. */
export const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/iu;

/**
 * Whether a rework sent now would re-open the inspection's OWN step (D-299
 * owner ruling R8) — the only target whose order the inspection read reports.
 * True for a blank field (`take` then sends no ref, and §3.5a defaults to the
 * inspection's operation) and for that step's own id typed in; false for any
 * OTHER step named in the field, whose order this client does not know. A
 * half-typed id is not a target at all (`take` drops it and Confirm is already
 * blocked by `targetInvalid`), so it counts as blank here — the default is
 * what it would fall back to. Ids compare case-insensitively: UUID_SHAPE
 * accepts either case and the server's uuid type does not distinguish them.
 */
export function chosenTargetIsOwnStep(typedTarget: string, ownOperationId: string | null): boolean {
  const typed = typedTarget.trim();
  if (!UUID_SHAPE.test(typed)) return true;
  return ownOperationId !== null && typed.toLowerCase() === ownOperationId.toLowerCase();
}

export function FailureActionSheet({
  inspection,
  guidedCut,
  reworkTargetWorkOrderStatus,
  arm,
  grants,
  onOutcome,
  onApplied,
}: {
  readonly inspection: QualityInspection;
  /** §3.5d's server-computed eligibility (D-158). OPTIONAL at this boundary
   * and not because the contract makes it so: the floor's inspection read is a
   * versionless `useCachedRead` snapshot, so a device that cached this
   * inspection before 07-C shipped hands the screen a payload without the
   * block, and dereferencing it would white-screen the execution screen in
   * exactly the place — offline, at the drum — where this product may not
   * fail. Its absence renders as the tenth class in guided-cut-action.tsx. */
  readonly guidedCut: QualityInspectionGuidedCut | undefined;
  /** The §3.1 status of the order a rework with no explicit ref would re-open
   * (D-299 ruling G5), through `reworkTargetWorkOrderStatusOf`: a status, `null`
   * (the server knows no target) or `undefined` (a payload from before the
   * field). OPTIONAL at this boundary for the same cached-floor-read reason as
   * `guidedCut`; only a known non-live status renders Rework dead. */
  readonly reworkTargetWorkOrderStatus?: QualityReworkTargetWorkOrderStatus | null | undefined;
  readonly arm: FailureActionArm;
  readonly grants: FailureActionGrants;
  readonly onOutcome: (outcome: SheetOutcome) => void;
  readonly onApplied?: (() => void) | undefined;
}) {
  const { t, i18n } = useTranslation();
  const [active, setActive] = useState<SheetAction | undefined>(undefined);
  const [note, setNote] = useState("");
  const [reworkTarget, setReworkTarget] = useState("");
  // D-299 G5/R8 — the OWN step's order, from the server's read, never
  // re-derived. It judges only a rework aimed at that step (see the head note).
  const ownOrderDeadStatus = reworkTargetOrderNotLive(reworkTargetWorkOrderStatus)
    ? reworkTargetWorkOrderStatus
    : undefined;
  const aimsAtOwnStep = chosenTargetIsOwnStep(reworkTarget, inspection.work_order_operation_id);
  // The order of the own step Confirm would send to, when the read knows it.
  const chosenOrderDeadStatus = aimsAtOwnStep ? ownOrderDeadStatus : undefined;

  // D-299 R8 + round 4 — THE CHOSEN STEP IS ASKED ABOUT, NOT GUESSED. The door
  // decides on the step it would re-open, and only the server knows that
  // step's order, its status and — for a HELD step — whether THIS caller may
  // rework it (D-133, principal-aware since round 4). `arm.reworkTarget`
  // previews the door (`GET …/rework-target`) and Confirm is judged on the
  // answer:
  //
  //  * a DIFFERENT step named in the field is asked about by its id;
  //  * the OWN step (field blank, or its own id typed) is asked about with no
  //    ref (`null`), the door's default — unless the inspection read already
  //    says its order is finished, in which case that read answers and nothing
  //    is asked. The own step is the held case a planner meets first: a failed
  //    blocking in-process check holds its own step (spec 07 §3.4), and the
  //    round-3 sheet judged it on the order alone, so a planner's Confirm went
  //    live into D-133's 409;
  //  * an inspection that names no step, with a blank field, asks nothing —
  //    the door needs a ref, and its own 422 says so.
  //
  // `undefined` = ask nothing; `null` = ask about the door's default. One
  // preview per target; an answer for a target the form no longer holds is
  // dropped on arrival.
  const previewTarget: string | null | undefined =
    active !== "rework" || !arm.online
      ? undefined
      : !aimsAtOwnStep
        ? reworkTarget.trim().toLowerCase()
        : chosenOrderDeadStatus === undefined && inspection.work_order_operation_id !== null
          ? null
          : undefined;
  const [preview, setPreview] = useState<ReworkPreview | undefined>(undefined);
  const previewReworkTarget = arm.reworkTarget;
  useEffect(() => {
    if (previewTarget === undefined) return undefined;
    let current = true;
    setPreview({ target: previewTarget, state: "checking" });
    void previewReworkTarget({
      inspectionId: inspection.id,
      workOrderOperationId: previewTarget,
    }).then((outcome) => {
      if (!current) return;
      setPreview(reworkPreviewOfOutcome(previewTarget, outcome));
    });
    return () => {
      current = false;
    };
  }, [previewReworkTarget, inspection.id, previewTarget]);
  const chosenPreview =
    previewTarget !== undefined && preview?.target === previewTarget ? preview : undefined;
  // D-299 round 5: a target with no answer of its own yet reads CHECKING,
  // from the first render that holds it. The effect above sets 'checking' only
  // after that render commits, so for one commit the state is either empty or
  // the previous target's answer. Dropping the stale answer alone left that
  // commit with no verdict, which blocks nothing: Confirm went live for a step
  // nobody had asked about.
  const shownPreview: ReworkPreview | undefined =
    previewTarget !== undefined && chosenPreview === undefined
      ? { target: previewTarget, state: "checking" }
      : chosenPreview;
  // The CHOSEN target's verdict, whichever way it is known: the inspection
  // read for a finished own order, the door's preview for everything else.
  const chosenVerdict: ReworkVerdict | undefined =
    chosenOrderDeadStatus !== undefined
      ? { kind: "order_not_live", seqNo: undefined, workOrderStatus: chosenOrderDeadStatus }
      : reworkVerdictOf(shownPreview);

  // The subject a Quarantine button would hold — §2.7: a stock unit XOR a lot.
  // An operation-subject failure has no material to fence (module 04's
  // qc_hold already fences the work), so the button is ABSENT, not dead.
  const quarantineSubject =
    inspection.stock_unit_id !== null
      ? { stock_unit_id: inspection.stock_unit_id }
      : inspection.lot_id !== null
        ? // A lot is tenant-wide (spec 06 §2.2) so the manual door requires the
          // facility NAMED — the failing inspection's own is where the failure
          // was found (§2.7's placement mechanics).
          { lot_id: inspection.lot_id, facility_id: inspection.facility_id }
        : undefined;

  const offered: readonly SheetAction[] = [
    ...(grants.canRework ? (["rework"] as const) : []),
    ...(grants.canDispose ? (["scrap"] as const) : []),
    ...(grants.canQuarantine && quarantineSubject !== undefined ? (["quarantine"] as const) : []),
    ...(grants.canDispose ? (["accept_deviation"] as const) : []),
  ];

  // The fifth control's class, decided ONCE, from the server's block (D-181).
  const cut = guidedCutRender({ canCut: grants.canCut, block: guidedCut });

  if (offered.length === 0 && cut.kind === "absent") {
    // No authority to decide anything here — one sentence, not five dead
    // buttons (D-118). This is the successor of 07-A's dispositionPending
    // note, narrowed to the principals it is still true for.
    //
    // THE GUIDED CUT IS PART OF THIS TEST, and it is not a tidy-up: §7 gives
    // `floor_worker` `quality.cut.execute` and NONE of the other three codes,
    // so the persona §4.5 was written for — the person standing at the saw —
    // is precisely the principal for whom the old four-way test was true and
    // the five-way one is false. Keeping the early return at four would have
    // rendered "this is a quality lead's decision" over the one control they
    // do hold.
    return (
      <p className="qrun-note qrun-sheet-lead" data-testid="qrun-sheet-noauthority">
        {t("quality.run.sheet.noAuthority")}
      </p>
    );
  }

  const noteRequired = active === "accept_deviation" || active === "quarantine";
  const noteMissing = noteRequired && note.trim().length === 0;
  const reworkTargetInvalid =
    active === "rework" && reworkTarget.trim().length > 0 && !UUID_SHAPE.test(reworkTarget.trim());
  // D-299 R8: Confirm — not the button, not the field — is what a dead chosen
  // target blocks, and the sentence is the one builder the §4.6 dialog uses
  // too (owner ruling Q4). It names the continuations only as the reader's
  // own when they hold them (owner ruling R3): scrap and accept-deviation ride
  // `quality.resolution.dispose`, and a planner who holds only the rework code
  // is told who takes them instead. A held step this caller cannot rework
  // reads D-133's who-sentence, never "will reopen" (round-3 refutation).
  const reworkTargetBlockedReason: string | undefined =
    active !== "rework" || reworkTargetInvalid
      ? undefined
      : reworkBlockedSentence(t, chosenVerdict, {
          door: "resolve",
          canDispose: grants.canDispose,
          locale: i18n.language,
        });
  const confirmBlockedReason = noteMissing
    ? t(
        active === "quarantine"
          ? "quality.run.sheet.reasonRequired"
          : "quality.run.sheet.noteRequired",
      )
    : reworkTargetInvalid
      ? t("quality.run.sheet.targetInvalid")
      : reworkTargetBlockedReason;

  const take = async (action: SheetAction): Promise<void> => {
    const trimmed = note.trim();
    if (action === "quarantine") {
      if (quarantineSubject === undefined) return;
      const outcome = await arm.quarantine({
        body: { ...quarantineSubject, reason: trimmed },
      });
      if (outcome.kind === "done") {
        onOutcome({ kind: "quarantined", holdNo: outcome.result.hold_no });
        setActive(undefined);
        setNote("");
        onApplied?.();
      } else {
        onOutcome({
          kind: "refused",
          action,
          message: outcome.message,
          detail: outcome.detail,
          requestId: outcome.requestId,
        });
        // R1 — a refusal ALSO re-reads: the server refused because it holds a
        // truth this screen may not (the lost-response retry replays into a
        // 409 over an action that already succeeded), and only a re-read
        // swaps the stale pending sheet for the resolved line.
        onApplied?.();
      }
      return;
    }
    const target = reworkTarget.trim();
    const outcome = await arm.resolve({
      inspectionId: inspection.id,
      body: {
        kind: action,
        ...(trimmed.length > 0 ? { notes: trimmed } : {}),
        ...(action === "rework" && UUID_SHAPE.test(target)
          ? { work_order_operation_id: target }
          : {}),
      },
    });
    if (outcome.kind === "done") {
      onOutcome({
        kind: "resolved",
        action,
        reworkOperationId: outcome.result.rework_operation_id,
        wasteEventId: outcome.result.waste_event_id,
        holdId: outcome.result.hold_id,
        scrapValuation:
          outcome.result.scrap_valuation !== undefined
            ? {
                costMinor: outcome.result.scrap_valuation.cost_minor,
                currency: outcome.result.scrap_valuation.currency,
              }
            : undefined,
      });
      setActive(undefined);
      setNote("");
      setReworkTarget("");
      onApplied?.();
    } else {
      onOutcome({
        kind: "refused",
        action,
        message: outcome.message,
        detail: outcome.detail,
        requestId: outcome.requestId,
      });
      // R1 — same rule as the quarantine branch: a refusal re-reads truth.
      onApplied?.();
    }
  };

  return (
    <div className="qrun-sheet" data-testid="qrun-action-sheet">
      <p className="qrun-sheet-lead">{t("quality.run.sheet.lead")}</p>
      <div className="qrun-acts" data-testid="qrun-acts">
        {offered.map((action) => {
          // D-299 G5/R8: the own step's order is a FACT the Rework slab states,
          // not a reason the button is dead — a step on a live order can
          // still be named in the form. Offline is the one thing that kills
          // the button, so offline is the reason it shows when both hold.
          const orderNote = action === "rework" && arm.online ? ownOrderDeadStatus : undefined;
          return (
            <button
              key={action}
              type="button"
              className={`qrun-act qrun-act-${action === "scrap" ? "danger" : "plain"}`}
              aria-pressed={active === action}
              // OFFLINE: the control stays, dead, with its reason INSIDE the
              // slab (D-118) — an unexplained dead button on a station tablet is
              // indistinguishable from a broken one.
              disabled={!arm.online || arm.busy}
              data-testid={`qrun-act-${action}`}
              onClick={() => {
                setActive((prior) => (prior === action ? undefined : action));
                setNote("");
                setReworkTarget("");
              }}
            >
              <span className="qrun-act-label">{t(`quality.run.sheet.act.${action}`)}</span>
              {!arm.online ? (
                <span
                  className="qrun-act-reason"
                  data-testid={`qrun-act-${action}-reason`}
                  data-reason="offline"
                >
                  {t("quality.run.sheet.offline")}
                </span>
              ) : orderNote !== undefined ? (
                <span
                  className="qrun-act-reason"
                  data-testid={`qrun-act-${action}-reason`}
                  data-reason="order_not_live"
                >
                  {t("quality.run.sheet.reworkOrderNotLive", {
                    status: t(`production.status.wo.${orderNote}`),
                  })}
                </span>
              ) : null}
            </button>
          );
        })}

        {/* THE FIFTH (07-C). It sits with the other four because it is one of
            §3.5's five answers to "what happens to this material", and it is
            rendered by its own component because its four not-available shapes
            are a decision the server takes and this sheet only spells. */}
        <GuidedCutAction
          render={cut}
          block={guidedCut}
          inspectionId={inspection.id}
          stockUnitId={inspection.stock_unit_id}
          arm={arm}
          canRemeasure={grants.canRemeasure}
          {...(onApplied !== undefined ? { onApplied } : {})}
        />
      </div>

      {/* A Rework form stays open when a refetch shows the own step's order
          finished (D-299 R8): its target field is still a way through, and
          its Confirm now carries the order's reason instead of the 409. */}
      {active !== undefined && arm.online ? (
        <div className="qrun-sheet-form" data-testid="qrun-sheet-form">
          <label className="qrun-field">
            {t(
              active === "quarantine"
                ? "quality.run.sheet.reasonLabel"
                : active === "accept_deviation"
                  ? "quality.run.sheet.noteMandatoryLabel"
                  : "quality.run.sheet.noteLabel",
            )}
            <input
              className="qrun-input"
              value={note}
              data-testid="qrun-sheet-note"
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          </label>
          {active === "rework" ? (
            <label className="qrun-field">
              {t("quality.run.sheet.targetLabel")}
              <input
                className="qrun-input"
                value={reworkTarget}
                placeholder={t("quality.run.sheet.targetHint")}
                data-testid="qrun-sheet-target"
                onChange={(e) => {
                  setReworkTarget(e.target.value);
                }}
              />
            </label>
          ) : null}
          {/* D-299 R8: the named step the door WOULD re-open, said back by
              its sequence number, so a mistyped id that happens to resolve
              reads as the wrong step before Confirm rather than after. Only
              for a verdict that blocks nothing — never beside a Confirm the
              same preview blocked (round-3 refutation), and never for a held
              step this caller cannot rework (round 4). */}
          {active === "rework" && !aimsAtOwnStep && chosenVerdict?.kind === "live" ? (
            <p className="qrun-note" role="status" data-testid="qrun-sheet-target-ok">
              {t("quality.run.sheet.reworkTargetLive", { seq: chosenVerdict.seqNo })}
            </p>
          ) : null}
          <Button
            variant={active === "scrap" ? "danger" : "primary"}
            data-testid="qrun-sheet-confirm"
            disabled={confirmBlockedReason !== undefined || arm.busy}
            busy={arm.busy}
            busyLabel={t("quality.run.saving")}
            onClick={() => {
              void take(active);
            }}
          >
            {t(`quality.run.sheet.confirm.${active}`)}
          </Button>
          {/* D-118 — a dead confirm says WHY, at the control. */}
          {confirmBlockedReason !== undefined ? (
            <p className="qrun-blocked" role="status" data-testid="qrun-sheet-blocked">
              {confirmBlockedReason}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** The last action's outcome, rendered by the SCREEN so it survives the
 * refetch that swaps the pending sheet for the resolved line. */
export function SheetOutcomeNote({
  outcome,
  locale,
}: {
  readonly outcome: SheetOutcome;
  readonly locale: string;
}) {
  const { t } = useTranslation();
  if (outcome.kind === "refused") {
    return (
      <div
        className="qrun-settle-bad qrun-sheet-outcome"
        role="alert"
        data-testid="qrun-sheet-refused"
      >
        <p className="qrun-sheet-outcome-line">{outcome.message}</p>
        {outcome.detail !== undefined && outcome.detail !== outcome.message ? (
          <p className="qrun-sheet-outcome-line">{outcome.detail}</p>
        ) : null}
        {outcome.requestId !== undefined ? (
          <p className="qrun-sheet-outcome-line">
            {t("quality.common.requestId")}: <code className="ref-id">{outcome.requestId}</code>
          </p>
        ) : null}
      </div>
    );
  }
  if (outcome.kind === "quarantined") {
    return (
      <p className="qrun-settle-ok qrun-sheet-outcome" role="status" data-testid="qrun-sheet-done">
        {t("quality.run.sheet.done.quarantine", { holdNo: outcome.holdNo })}
      </p>
    );
  }
  return (
    <div className="qrun-settle-ok qrun-sheet-outcome" role="status" data-testid="qrun-sheet-done">
      <p className="qrun-sheet-outcome-line">{t(`quality.run.sheet.done.${outcome.action}`)}</p>
      {outcome.action === "rework" && outcome.reworkOperationId !== null ? (
        <p className="qrun-sheet-outcome-line">
          {t("quality.run.sheet.reworkRef")}{" "}
          <code className="ref-id">{outcome.reworkOperationId}</code>
        </p>
      ) : null}
      {outcome.action === "scrap" ? (
        <p className="qrun-sheet-outcome-line" data-testid="qrun-sheet-valuation">
          {/* §7's second bullet, the client half: the valuation travels with
              module 05's OWN visibility set, and when it is absent the screen
              LABELS the omission instead of leaving an invisible boundary
              between two adjacent money numbers. Absence stays absence on the
              wire; the label is copy. */}
          {outcome.scrapValuation !== undefined
            ? t("quality.run.sheet.scrapValuation", {
                amount: formatMinor(
                  outcome.scrapValuation.costMinor,
                  outcome.scrapValuation.currency,
                  locale,
                ),
              })
            : t("quality.run.sheet.scrapValuationElsewhere")}
        </p>
      ) : null}
    </div>
  );
}
